import { lazy, Suspense, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMe } from '../lib/api';
import type { MemberSpaceDetail } from '../lib/api';
import { membersSummary, spaceDisplayName } from '../lib/spaceIdentity';
import { useSpaceScheduleState } from '../hooks/useSpaceScheduleState';
import { ConnectAgentModal } from './ConnectAgentModal';
import { InviteModal } from './InviteModal';
import { MessageSearchBar } from './MessageSearchBar';
import { MuteToggle } from './MuteToggle';
import { SpaceStateChip } from './SpaceStateChip';
import { ThreadScheduleAffix } from './ThreadScheduleAffix';

// The members sheet is opened on demand (manage ▾), so it rides its own lazy
// chunk instead of the entry bundle — administration you rarely summon doesn't
// belong in the weight of every thread open.
const MembersSheet = lazy(() =>
  import('./MembersSheet').then((m) => ({ default: m.MembersSheet })),
);

interface SpaceHeaderProps {
  room: MemberSpaceDetail;
  /** The thread's search query changes flow up through here — the control now
   *  lives in the header row proper (T-0200). */
  onSearchChange: (query: string) => void;
}

export function SpaceHeader({ room, onSearchChange }: SpaceHeaderProps) {
  const [membersOpen, setMembersOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const { state: scheduleState, latestFire } = useSpaceScheduleState(room.room_id);

  const title = spaceDisplayName(room.description, room.members, me?.user_id);
  const summary = membersSummary(room.members, me?.user_id);

  return (
    <header className="space-header">
      <div className="space-header-top">
        <h2 className="space-header-title">{title}</h2>
        <SpaceStateChip roomState={room.state} scheduleState={scheduleState} />
        <ThreadScheduleAffix
          scheduleState={scheduleState}
          firedAt={latestFire?.fired_at}
        />
        <div className="space-header-spacer" />
        <MessageSearchBar onQueryChange={onSearchChange} />
        <MuteToggle roomId={room.room_id} />
      </div>

      <div className="space-header-members-row">
        <span className="space-header-avatars" aria-hidden="true">
          {summary.avatars.map((glyph, i) => (
            <span key={i} className="space-header-avatar">
              {glyph}
            </span>
          ))}
        </span>
        <span className="space-header-members-summary">
          {summary.count} {summary.count === 1 ? 'member' : 'members'}
          {summary.parties ? ` — ${summary.parties}` : ''}
        </span>
        <button
          type="button"
          className="space-header-manage"
          onClick={() => setMembersOpen(true)}
          aria-haspopup="dialog"
        >
          manage ▾
        </button>
      </div>

      {membersOpen && (
        <Suspense fallback={null}>
          <MembersSheet
            room={room}
            onClose={() => setMembersOpen(false)}
            onInviteWithLink={() => {
              setMembersOpen(false);
              setInviteOpen(true);
            }}
            onAddAgent={() => {
              setMembersOpen(false);
              setAgentModalOpen(true);
            }}
          />
        </Suspense>
      )}
      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} />}
      {agentModalOpen && (
        <ConnectAgentModal
          space={{
            id: room.room_id,
            name: room.description || 'this space',
          }}
          onClose={() => setAgentModalOpen(false)}
        />
      )}
    </header>
  );
}
